import { Close, MoreVert } from "@mui/icons-material";
import {
  Alert,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { createFileRoute, notFound, Outlet, useMatches } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { CopyTextField } from "@/components/core/copy-textfield";
import { ErrorDisplay } from "@/components/core/error-display";
import { CardLoadingSkeleton } from "@/components/core/loading-skeleton";
import { Link, LinkButton } from "@/components/core/tanstack-link";
import { CustomerAddNote } from "@/components/customer/add-note";
import { onError } from "@/components/core/display-error";
import { DeeplinkCustomer } from "@/helpers/deeplink";
import { providerToName } from "@/helpers/labels";
import {
  CustomerDetailOut,
  EmailIdentityOut,
  NoteTroveType,
  getCustomer,
  getGetCustomerQueryKey,
  getGetWorkflowsQueryKey,
  getWorkflows,
  invoicesArchive,
  useGetCurrentOrganization,
  useGetCustomer,
  useUnpinNote,
} from "@/models/backend";
import { AxiosError } from "axios";
import { CountryCode, parsePhoneNumberFromString, PhoneNumber } from "libphonenumber-js";
import { queryClient } from "@/helpers/tanstack-client";
import { formatEmail } from "@/helpers/email";
import { commaSeparate } from "@/helpers/comma-separate";
import { CustomerStatusChip } from "@/components/customer/state-button";
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  OnChangeFn,
  Table,
  useTable,
  ColumnVisibilityState,
  tableFeatures,
  columnVisibilityFeature,
} from "@tanstack/react-table";
import { WorkflowSelect } from "@/components/customer/workflow-select";
import dayjs from "dayjs";

export const Route = createFileRoute("/_authenticated/_contained/customers/$customerId")({
  component: RouteComponent,
  loader: async ({ params: { customerId } }) => {
    await Promise.all([
      queryClient.query({
        queryFn: () => getCustomer(customerId),
        queryKey: getGetCustomerQueryKey(customerId),
        staleTime: "static",
      }),
      queryClient.query({
        queryFn: getWorkflows,
        queryKey: getGetWorkflowsQueryKey(),
        staleTime: "static",
      }),
    ]);
  },
});

const features = tableFeatures({
  columnVisibilityFeature,
});

const MoreButton = ({
  customer,
  table,
}: {
  customer: CustomerDetailOut;
  table: Table<typeof features, CustomerDetailOut>;
}) => {
  const [menuOpen, setMenuOpen] = useState<HTMLButtonElement | null>(null);
  const menuId = useId();
  const [showCopy, setShowCopy] = useState(false);
  const [menuView, setMenuView] = useState<"main" | "headerFields">("main");
  const [downloading, setDownloading] = useState(false);

  const toggleField = (field: string) => {
    table.setColumnVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };
  const handleDownloadInvoices = async () => {
    setDownloading(true);
    const data = await invoicesArchive(customer.id);
    const archive = URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = archive;
    link.download = `${customer.name} invoices ${dayjs().format("D MMMM YY")}.zip`;
    link.target = "_blank";
    link.click();
    URL.revokeObjectURL(archive);
    setDownloading(false);
    setMenuOpen(null);
  };

  return (
    <>
      <Button
        sx={{ minWidth: "0px", padding: "1px" }}
        variant="outlined"
        color="secondary"
        aria-controls={menuId}
        aria-haspopup="true"
        aria-expanded={menuOpen ? "true" : undefined}
        onClick={(e) => setMenuOpen(menuOpen ? null : e.currentTarget)}
      >
        <MoreVert />
      </Button>
      <Menu
        id={menuId}
        anchorEl={menuOpen}
        open={menuOpen !== null}
        onClose={() => {
          setMenuOpen(null);
          setMenuView("main");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menuView == "headerFields"
          ? [
              <ListItemText key="title">Title fields</ListItemText>,
              ...table
                .getAllLeafColumns()
                .filter((col) => col.parent?.id == "title")
                .map((col) => (
                  <MenuItem key={col.id} onClick={() => toggleField(col.id)}>
                    <Checkbox checked={col.getIsVisible()} size="small" sx={{ p: 0, pr: 1 }} />
                    <ListItemText>{col.columnDef.header as string}</ListItemText>
                  </MenuItem>
                )),
              <Divider key="divider" />,
              <ListItemText key="info">Info fields</ListItemText>,
              ...table
                .getAllLeafColumns()
                .filter((col) => col.parent?.id == "info")
                .map((col) => (
                  <MenuItem key={col.id} onClick={() => toggleField(col.id)}>
                    <Checkbox checked={col.getIsVisible()} size="small" sx={{ p: 0, pr: 1 }} />
                    <ListItemText>{col.columnDef.header as string}</ListItemText>
                  </MenuItem>
                )),
            ]
          : [
              <MenuItem component={DeeplinkCustomer} customer={customer} key="deeplink">
                <ListItemText>View in {providerToName(customer.provider)}</ListItemText>
              </MenuItem>,
              ...(customer.portal_url != null
                ? [
                    <MenuItem
                      onClick={() => {
                        setShowCopy(true);
                        setMenuOpen(null);
                      }}
                      key="portal"
                    >
                      <ListItemText>Share customer portal</ListItemText>
                    </MenuItem>,
                  ]
                : []),
              <MenuItem onClick={() => setMenuView("headerFields")} key="headerConfig">
                <ListItemText>Configure header</ListItemText>
              </MenuItem>,
              <MenuItem key="downloadInvoices" onClick={handleDownloadInvoices}>
                {downloading && (
                  <ListItemIcon>
                    <CircularProgress size={20} />
                  </ListItemIcon>
                )}
                <ListItemText>Download invoices</ListItemText>
              </MenuItem>,
            ]}
      </Menu>
      <Dialog open={showCopy} onClose={() => setShowCopy(false)}>
        <DialogTitle>Share invoice link</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>Share this link with your customer:</Typography>
            <CopyTextField value={customer.portal_url ?? ""} sx={{ maxWidth: "600px" }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCopy(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const createColumn = createColumnHelper<typeof features, CustomerDetailOut>();

function RouteComponent() {
  const { data: organization } = useGetCurrentOrganization();
  const [addNote, setAddNote] = useState<{
    type?: NoteTroveType;
    eventId?: string;
    workflowId?: string;
  } | null>(null);
  const [confirmUnpin, setConfirmUnpin] = useState(false);

  const columns = [
    createColumn.group({
      id: "title",
      columns: createColumn.columns([
        createColumn.accessor((row) => row.alias ?? row.name ?? "unknown", {
          id: "name",
          header: "Name",
        }),
        createColumn.accessor("account_number", {
          header: "Account number",
          meta: { defaultHidden: true },
        }),
      ]),
    }),
    createColumn.group({
      id: "info",
      columns: createColumn.columns([
        createColumn.accessor("primary_contacts", {
          header: "Primary contacts",
          cell: ({ getValue }) => {
            const primaryContacts: string[] = getValue().map(
              (c: CustomerDetailOut["primary_contacts"][0]) =>
                c.email != null
                  ? formatEmail(c as EmailIdentityOut)
                  : `${c.first_name ?? ""} ${c.last_name ?? ""}`,
            );

            return primaryContacts.length > 0 ? (
              <Typography variant="body2">
                Primary contact{primaryContacts.length !== 1 && "s"}:{" "}
                {commaSeparate(
                  primaryContacts.map((contact) => (
                    <Link key={contact} to="." search={{ tab: "contacts" }}>
                      {contact}
                    </Link>
                  )),
                )}
              </Typography>
            ) : null;
          },
        }),
        createColumn.accessor("phones", {
          header: "Phone numbers",
          cell: ({ getValue, row }) => {
            const phones: [string, PhoneNumber | undefined][] = getValue().map(
              (phone: CustomerDetailOut["phones"][0]) =>
                [
                  phone.type == "ddi"
                    ? "DDI"
                    : phone.type.slice(0, 1).toUpperCase() + phone.type.slice(1),
                  parsePhoneNumberFromString(
                    phone.number,
                    (organization?.locale.length ?? 0) >= 5
                      ? (organization?.locale.slice(3) as CountryCode)
                      : undefined,
                  ),
                ] as const,
            );
            return phones.map(
              ([type, phone]) =>
                phone && (
                  <Typography variant="body2" key={type}>
                    {type} number:{" "}
                    <Link
                      to={phone.getURI()}
                      params={{ customerId: row.id }}
                      search={{ tab: "contacts" }}
                    >
                      {phone.formatInternational()}
                    </Link>
                  </Typography>
                ),
            );
          },
        }),
      ]),
    }),
  ];

  const visibilityStorageKey = `customer-detail-visibility`;
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() => {
    const defaults: ColumnVisibilityState = {};
    const extract = (columns: readonly ColumnDef<typeof features, CustomerDetailOut>[]) => {
      for (const col of columns) {
        if ("columns" in col && col.columns) {
          extract(col.columns);
          continue;
        }
        const id = col.id ?? ("accessorKey" in col ? col.accessorKey : undefined);
        if (id) {
          if (col.meta?.defaultHidden) {
            defaults[id] = false;
          } else {
            defaults[id] = true;
          }
        }
      }
    };
    extract(columns);
    if (!visibilityStorageKey) return defaults;
    try {
      const stored = localStorage.getItem(visibilityStorageKey);
      return stored ? { ...defaults, ...JSON.parse(stored) } : defaults;
    } catch {
      return defaults;
    }
  });
  const handleColumnVisibilityChange: OnChangeFn<ColumnVisibilityState> = (updater) => {
    setColumnVisibility((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (visibilityStorageKey) {
        try {
          localStorage.setItem(visibilityStorageKey, JSON.stringify(next));
        } catch {}
      }
      return next;
    });
  };

  const { customerId } = Route.useParams();
  const search = Route.useSearch();
  const matches = useMatches();
  const isCompose = matches.some(
    (m) => m.loaderData && "crumb" in m.loaderData && m.loaderData?.crumb === "Compose",
  );
  const {
    data: customer,
    isLoading: isLoadingCustomer,
    isError: isErrorCustomer,
    error: customerError,
    refetch: refetchCustomer,
  } = useGetCustomer(customerId);
  const queryClient = useQueryClient();
  const { mutate: unpinNote } = useUnpinNote();

  const handleUnpin = () => {
    unpinNote(
      { customer: customerId },
      {
        onSuccess: () => {
          enqueueSnackbar("Note unpinned", { variant: "success" });
          queryClient.invalidateQueries({ queryKey: getGetCustomerQueryKey(customerId) });
        },
        onError: onError({ prefix: "Failed to unpin note" }),
      },
    );
    setConfirmUnpin(false);
  };

  const table = useTable({
    features,
    columns,
    state: {
      columnVisibility,
    },
    data: customer ? [customer] : [],
    onColumnVisibilityChange: handleColumnVisibilityChange,
  });
  const pageTitle =
    table
      .getRow("0")
      .getVisibleCells()
      .filter((c) => c.column.parent?.id == "title")
      .map((value) => value.renderValue())
      .filter((v) => v)
      .join(" · ") || customer?.name;
  const pageInfo = table
    .getRow("0")
    .getVisibleCells()
    .filter((c) => c.column.parent?.id == "info")
    .map((cell) => flexRender(cell.column.columnDef.cell, cell.getContext()));

  if (isErrorCustomer) {
    if (customerError instanceof AxiosError && customerError.status === 404) {
      throw notFound();
    }
    return (
      <ErrorDisplay
        title="Failed to load customer"
        error={customerError}
        onRetry={() => refetchCustomer()}
      />
    );
  }

  if (isLoadingCustomer) {
    return <CardLoadingSkeleton />;
  }

  if (!customer) {
    return null;
  }

  return (
    <Stack spacing={4}>
      <Breadcrumbs separator=">" aria-label="breadcrumb">
        <Link to="/customers">Customers</Link>
        {customer.parents.map((customer) => (
          <Link
            key={customer.id}
            to="/customers/$customerId"
            params={{ customerId: customer.id }}
            search={search}
          >
            {customer.name}
          </Link>
        ))}
        {isCompose ? (
          [
            <Link to="/customers/$customerId" params={{ customerId: customerId }} key="customer">
              {customer.name}
            </Link>,
            <Typography key="compose">Compose</Typography>,
          ]
        ) : (
          <Typography>{customer.name}</Typography>
        )}
      </Breadcrumbs>
      <Stack spacing={1}>
        <Stack
          spacing={1}
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "start" }}
        >
          <Stack spacing={1} sx={{ alignItems: "start" }}>
            <Typography variant="h3">{pageTitle}</Typography>
            {customer.status != null && <CustomerStatusChip status={customer.status} />}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ textWrap: "nowrap" }}>
            <WorkflowSelect
              customer={customer}
              onChange={({ type, workflowId }) => setAddNote({ type, workflowId })}
            />
            <Button
              variant="contained"
              color="info"
              onClick={() => {
                setAddNote({});
              }}
            >
              Add note
            </Button>
            <LinkButton
              variant="outlined"
              color="secondary"
              to="/customers/$customerId/compose"
              from="/customers/$customerId"
            >
              Compose new
            </LinkButton>
            <MoreButton customer={customer} table={table} />
          </Stack>
        </Stack>
        {pageInfo}
      </Stack>
      {customer.pinned_note && (
        <Alert
          severity="info"
          sx={{
            "& .MuiAlert-message": {
              whiteSpace: "pre-wrap",
              "-webkit-line-clamp": "5",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              "-webkit-box-orient": "vertical",
              overflow: "hidden",
            },
          }}
          action={
            <Stack direction="row" sx={{ alignItems: "center" }}>
              <Button
                color="inherit"
                size="small"
                sx={{ whiteSpace: "nowrap" }}
                onClick={() => setAddNote({ eventId: customer.pinned_note!.event_id ?? undefined })}
              >
                Edit note
              </Button>
              <IconButton size="small" color="inherit" onClick={() => setConfirmUnpin(true)}>
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          {customer.pinned_note.text}
        </Alert>
      )}
      <Outlet />
      <CustomerAddNote
        customer={customer}
        open={addNote !== null}
        onClose={() => setAddNote(null)}
        selectedInvoices={[]}
        forceType={addNote?.type}
        eventId={addNote?.eventId}
        workflowId={addNote?.workflowId}
      />
      <Dialog open={confirmUnpin} onClose={() => setConfirmUnpin(false)}>
        <DialogTitle>Unpin note?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the pinned note from the top of this customer's page.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button onClick={() => setConfirmUnpin(false)}>Cancel</Button>
          <Button onClick={handleUnpin} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
